import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Component, DestroyRef, inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, Subject, Subscription, tap } from 'rxjs';
import {
  ListItem,
  ListOperation,
  ListOperations,
  ListSearchStateService,
  PaginatedScrollingListService,
  ScrollingListStateService,
} from './services';
import { FoldersApiService } from 'src/app/api/services/folders-api.service';
import { FolderItem } from 'src/app/api/services/folder';
import { RvDraggableService, RvDraggableConfig } from 'src/app/rv-draggable';
import { TouchCapabilityService } from 'src/app/shared/services/touch-capability.service';

@Component({
  template: '',
  standalone: false,
})
export abstract class PageWithListComponent<T> {
  private scrollSubscription: Subscription;
  private viewport: CdkVirtualScrollViewport;
  /**
   * @param viewport - The scrolling viewport
   * Make sure that the <cdk-virtual-scroll-viewport> element has the
   * #scrollViewport template reference variable
   */
  @ViewChild('scrollViewport')
  set scrollViewport(viewport: CdkVirtualScrollViewport) {
    this.viewport = viewport;
    if (!viewport) {
      this.scrollSubscription?.unsubscribe();
      return;
    }
    this.scrollSubscription = this.registerScrollView<T>(viewport)
      .pipe(takeUntilDestroyed(this.getDestroyRef()))
      .subscribe();
  }

  private onScrollSubject = new Subject<void>();

  private registerScrollView<T>(scrollElement: CdkVirtualScrollViewport) {
    return scrollElement.scrolledIndexChange.pipe(
      debounceTime(100),
      tap(() => this.onScrollSubject.next()),
      tap(
        (index) =>
          index !== 0 &&
          scrollElement.getRenderedRange().end >=
            scrollElement.getDataLength() * 0.8 &&
          !this.listState.isLoadingItems() &&
          Boolean(this.listService.list().length) &&
          this.listService.load(),
      ),
    );
  }

  abstract getDestroyRef(): DestroyRef;
  abstract listOperations: ListOperations<T>;

  protected listService = inject<PaginatedScrollingListService<T>>(
    PaginatedScrollingListService,
  );
  protected listState = inject(ScrollingListStateService<T>);
  protected searchState = inject(ListSearchStateService);
  protected list = this.listService.list;
  protected loadingItems = toSignal(this.listState.loadingItems());
  protected error = toSignal(this.listState.error());
  private readonly touchCapabilityService = inject(TouchCapabilityService);

  canUseFolders(): boolean {
    return this.listService.areFoldersSupported();
  }

  protected draggableService: RvDraggableService<T, FolderItem>;

  private lastSelectedIndex: number | null = null;

  protected get onScroll() {
    return this.onScrollSubject.asObservable();
  }

  protected checkViewportSize() {
    setTimeout(() => {
      if (this.viewport) {
        this.viewport.checkViewportSize();
      }
    });
  }

  protected async handleOperationClick(
    operationName: string,
    item: ListItem<T>,
  ) {
    await this.executeOperation(operationName, item);
  }

  private async executeOperation(operationName: string, payload: ListItem<T>) {
    const operation = this.findOperation(
      this.listOperations.operations,
      operationName,
    );
    if (operation && operation.onClick) {
      operation.onClick(false, [payload]);
    }
  }

  private findOperation(
    operations: ListOperation<T>[],
    operationName: string,
  ): ListOperation<T> | undefined {
    for (const operation of operations) {
      if (operation.name === operationName) {
        return operation;
      }

      const child = this.findOperation(operation.children ?? [], operationName);
      if (child) {
        return child;
      }
    }

    return undefined;
  }

  protected async handleBulkOperation(
    operationName: string,
    items: ListItem<T>[],
    args: any,
  ) {
    await this.executeBulkOperation(operationName, items, args);
  }

  private async executeBulkOperation(
    operationName: string,
    payload: ListItem<T>[],
    args: any,
  ) {
    const operation: ListOperation<T> = this.listOperations.operations.find(
      (operation) => operation.name === operationName,
    );
    if (operation && operation.onClick) {
      operation.onClick(true, payload, args);
    }
  }

  protected updateMoveAfterAction(moveActionName = 'Move') {
    const moveOperation = this.listOperations.operations.find(
      (operation) => operation.name === moveActionName,
    );
    if (!moveOperation) return;
    moveOperation.afterAction = (listItem, error) => {
      if (!error && !moveOperation.groupBy) {
        this.listState.remove(listItem);
      }
    };
  }

  protected initDraggableService(
    containersSelector: string,
    appendTo: string,
    options?: Pick<RvDraggableConfig<T, FolderItem>, 'handleSelector'>,
  ) {
    this.draggableService?.destroy();
    this.draggableService = new RvDraggableService<T, FolderItem>({
      items: this.listService.list().map((item) => item.item) as (
        | T
        | FolderItem
      )[],
      getSelectedItems: this.listState.getSelected,
      onItemDrop: this.handleItemDrop,
      containersSelector,
      appendTo,
      touchCapable$: this.touchCapabilityService.touchCapable$,
      ...options,
    });

    this.getDestroyRef().onDestroy(() => {
      this.draggableService?.destroy();
    });
  }

  protected handleItemDrop = (
    itemIds: string[],
    folderId: string,
    idProperty: string = 'id',
  ) => {
    const items = this.listService
      .list()
      .filter((item: ListItem<T & { [idProperty]: string }>) =>
        itemIds.includes(item.item[idProperty]),
      );
    this.listService.activeOperation = this.listOperations.operations.find(
      (operation) => operation.name === 'Move',
    );
    this.handleBulkOperation(
      'Move',
      items as ListItem<T>[],
      folderId === '' ? FoldersApiService.ROOT_FOLDER_ID : folderId,
    );
  };

  onCheckboxClick(event: MouseEvent, listItem: ListItem<T>, index: number) {
    event.stopPropagation();

    if (event.shiftKey && this.lastSelectedIndex !== null) {
      this.selectRange(this.lastSelectedIndex, index);
    } else {
      this.listState.select(listItem);
      if (listItem.selected) {
        this.lastSelectedIndex = index;
      } else if (this.listState.isAllUnselected()) {
        this.lastSelectedIndex = null;
      }
    }
  }

  private selectRange(startIndex: number, endIndex: number) {
    const list = this.listState.currentList();
    const start = Math.min(startIndex, endIndex);
    const end = Math.max(startIndex, endIndex);

    // Select all items in the range
    for (let i = start; i <= end; i++) {
      if (list[i]) {
        this.listState.select(list[i], true);
      }
    }
  }

  protected toggleSelection() {
    if (this.listState.isAllSelected()) {
      this.listState.deselectAll();
    } else {
      this.listState.selectAll();
    }
    this.lastSelectedIndex = null;
  }
}

export interface ScrollingList<T> {
  load: () => void;
  doSearch: () => void;
  sortBy: (sortBy: string, reverse?: boolean) => void;
  select: (item: T) => void;
  selectAll: () => void;
  getSelected: () => (T & { selected: boolean })[];
  loadingItems: boolean;
  apiError: string;
  errorMessage: string;
  items: {
    list: T[];
  };
}

export interface ScrollingListOperation {
  name: string;
  onClick: (skipBeforeBatchAction: boolean, selectedItems: any[]) => void;
}
